import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  TextField,
  Tooltip,
} from "@mui/material";
import { Box } from "@mui/material";
import materialRequest from "common/requests/asyncRequests";
import rackRequest from "common/requests/asyncRequests";
import supplierRequest from "common/requests/asyncRequests";
import { debounce } from "@mui/material";
import {
  AsyncOption,
  IAsyncDropdownModel,
  IDropdownModel,
} from "common/models/IDropdownModel";
import { GetDepartmentValuesByInstitute } from "features/department/api/hooks";
import { GetProgramByInstituteDepartment } from "features/program/api/hooks";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { GetSectionValues } from "features/section/api/hooks";
import { GetMaterialStatusValues } from "features/materialStatus/api/hooks";
import { GetInstituteByLibrary } from "features/institute/api/hooks";
import { ProgramModelForDropdown } from "features/program/api/types";
import AsyncSelect from "react-select/async";
import { SelectInstance } from "react-select";
import DatePickerElement from "components/DatePickerElement";
import { SelectField } from "components/@extended/SelectField";
import { AccessionBarCodeSchema, IAccessionBarCodeSchema } from "../api/types";
import MainCard from "components/MainCard";
import AnimateButton from "components/@extended/AnimateButton";
import LoadingButton from "@mui/lab/LoadingButton";
import { CloudDownloadOutlined, InfoCircleFilled } from "@ant-design/icons";
import api from "utils/api";
import useAuth from "hooks/useAuth";
import requests from "common/requests/asyncRequests";
import RadioButtonGroup from "components/RadioButtonGroup";

const AccessionBarCodes = () => {
  const { modules } = useAuth();
  const materialRef = useRef<SelectInstance<IAsyncDropdownModel> | null>(null);
  const rackRef = useRef<SelectInstance<IAsyncDropdownModel> | null>(null);
  const supplierRef = useRef<SelectInstance<IDropdownModel> | null>(null);
  const [loading, setLoading] = useState(false);
  const [isReportsChecked, setIsReportsChecked] = useState(false);
  const [isBorderChecked, setIsBorderChecked] = useState(false);

  const handleIsReportsChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setIsReportsChecked(event.target.checked);
  };
  const handleIsBorderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsBorderChecked(event.target.checked);
  };
  const apiURL = "api/Barcode/accessionBarCodes";
  const fileName = "AccessionBarcodes";
  const methods = useForm<IAccessionBarCodeSchema>({
    mode: "all",
    resolver: zodResolver(AccessionBarCodeSchema),
    defaultValues: {
      isReports: false,
      isBorder: false,
      accessionValueType: 1,
    },
  });

  const {
    register,
    reset,
    handleSubmit,
    control,
    watch,
    formState: { isSubmitting, errors },
  } = methods;

  const handleClearFilter = () => {
    reset();
    setIsReportsChecked(false);
    setIsBorderChecked(false);
    materialRef.current.clearValue();
    rackRef.current.clearValue();
    supplierRef.current.clearValue();
  };

  const onSubmit = handleSubmit(async (data) => {
    setLoading(true);
    if (isSubmitting) return;
    if (data.materialValue) {
      data.materialID = String(data.materialValue.value);
    } else {
      data.materialID = null;
    }
    if (data.rackValue) {
      data.rackID = String(data.rackValue.value);
    } else {
      data.rackID = null;
    }
    if (data.supplierValue) {
      data.supplierID = data.supplierValue.id;
    } else {
      data.supplierID = null;
    }

    if (data.accessionValueType == 2) {
      data.accessionNoValues = null;
      data.accessionNoStringValue = data.accessionNoStringValue?.trim();
      if (data.accessionNoStringValue) {
        data.accessionNoStringValue = data.accessionNoStringValue
          .replace(/[\r\n\t\s]+/g, " ")
          .trim()
          ?.split(/[\r\n\t\s,]+/g) // split on newlines, spaces, or commas
          .filter((item) => item.trim() !== "")
          .join("\n");
      }
      data.accessionNoString = data.accessionNoStringValue
        ?.split(/[\r\n\t\s]/g)
        .filter((item) => item.trim() != "");
    } else if (data.accessionValueType == 1) {
      data.accessionNoString = null;
      data.accessionNoValues = data.accessionNo?.map((c) => c.id);
    }

    api.downloadZip(apiURL, fileName, data, setLoading);
  });
  const materialOptions: IAsyncDropdownModel[] = [];
  const handleMaterialSearch = debounce(async (query: string, callback) => {
    const options = await materialRequest.getMaterialAutoComplete(query, null);
    callback(options.apiData || []);
  }, 500);

  const rackOptions: IAsyncDropdownModel[] = [];
  const handleRackSearch = debounce(async (query: string, callback) => {
    const options = await rackRequest.getRackAutoComplete(query, null);
    callback(options.apiData || []);
  }, 500);

  const supplierOptions: IDropdownModel[] = [];
  const handleSupplierSearch = debounce(async (query: string, callback) => {
    const options = await supplierRequest.getSupplierAutoComplete(query, null);
    callback(options.apiData || []);
  }, 500);

  const { data: sectionResult } = GetSectionValues();
  const [sectionOptions, setSectionOptions] = useState<IDropdownModel[]>([]);
  useEffect(() => {
    setSectionOptions(sectionResult?.apiData || []);
  }, [sectionResult]);

  const { data: materialStatusResult } = GetMaterialStatusValues();
  const [materialStatusOptions, setMaterialStatusOptions] = useState<
    IDropdownModel[]
  >([]);
  useEffect(() => {
    setMaterialStatusOptions(materialStatusResult?.apiData || []);
  }, [materialStatusResult]);

  const [programModel, setProgramModel] = useState<ProgramModelForDropdown>();
  const { data: instituteQuery } = GetInstituteByLibrary();
  const [instituteOptions, setInstituteOptions] = useState<IDropdownModel[]>(
    [],
  );
  useEffect(() => {
    setInstituteOptions(instituteQuery?.apiData || []);
  }, [instituteQuery]);
  const instituteID = watch("instituteID");
  const departmentID = watch("departmentID");

  const { data: departmentQuery } = GetDepartmentValuesByInstitute(instituteID);
  const [departmentOptions, setDepartmentOptions] = useState<IDropdownModel[]>(
    [],
  );
  useEffect(() => {
    setDepartmentOptions(departmentQuery?.apiData || []);
  }, [departmentQuery]);

  useEffect(() => {
    if (departmentID == null || departmentID == undefined) {
      setProgramOptions([]);
    }

    if (instituteID == null || instituteID == undefined) {
      setDepartmentOptions([]);
      setProgramOptions([]);
    }
  }, [instituteID, departmentID]);

  useEffect(() => {
    setProgramModel({
      instituteID: instituteID,
      departmentID: departmentID,
    });
  }, [instituteID, departmentID]);

  const { data: programQuery } = GetProgramByInstituteDepartment(programModel);
  const [programOptions, setProgramOptions] = useState<IDropdownModel[]>([]);

  useEffect(() => {
    if (programQuery?.status == 200) {
      setProgramOptions(programQuery?.apiData || []);
    }
  }, [programQuery]);

  const accessionOptions: AsyncOption[] = [];
  const handleAccessionSearch = debounce(async (query: string, callback) => {
    const options = await requests.getAccessionNoIdName(query, null);
    callback(options.apiData || []);
  }, 500);

  const barcodeFormatOptions = [
    { id: 1, name: "A4 Portrait - 10 Rows * 4 Columns" },
    { id: 2, name: "A4 Portrait - 12 Rows * 3 Columns" },
    { id: 3, name: "A4 Portrait - 14 Rows * 4 Columns" },
    { id: 4, name: "A4 Portrait - 5 Rows * 2 Columns" },
    { id: 5, name: "A4 Portrait - 13 Rows * 5 Columns" },
    { id: 6, name: "A4 Landscape - 8 Rows * 5 Columns" },
    { id: 7, name: "A4 Portrait - 12 Rows * 4 Columns" },
    { id: 8, name: "50 MM × 25 MM – Roll Label" },
  ];

  const contentFormats = [
    { id: 1, name: "Only Barcode" },
    { id: 2, name: "Only Unique Code" },
    { id: 3, name: "Barcode and Unique Code" },
  ];

  const accessionValueType = watch("accessionValueType");
  useEffect(() => {
    if (accessionValueType == 1) {
      methods.setValue("accessionNo", null);
    }
    if (accessionValueType == 2) {
      methods.setValue("accessionNoString", null);
    }
  }, [accessionValueType]);
  return (
    <>
      <MainCard darkTitle title="Accession Barcodes" darktitleVariant={"h4"}>
        {modules?.barCode?.permissions?.Add ? (
          <>
            <Box component="form" onSubmit={onSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={6} md={4} lg={4}>
                  <SelectField
                    options={instituteOptions}
                    control={control}
                    name="instituteID"
                    label="Institute"
                    placeholder="Select Institute"
                  />
                </Grid>
                <Grid item xs={6} md={4} lg={4}>
                  <SelectField
                    options={departmentOptions}
                    control={control}
                    name="departmentID"
                    label="Department"
                    placeholder="Select Department"
                  />
                </Grid>
                <Grid item xs={6} md={4} lg={4}>
                  <SelectField
                    options={programOptions}
                    control={control}
                    name="programID"
                    label="Program"
                    placeholder="Select Program"
                  />
                </Grid>
                <Grid item xs={6} md={4} lg={4}>
                  <Controller
                    name="materialValue"
                    control={control}
                    render={({ field: { onChange, value, ref } }) => (
                      <AsyncSelect
                        ref={materialRef}
                        defaultValue={materialOptions}
                        styles={{
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999,
                          }),
                        }}
                        inputRef={ref}
                        getOptionLabel={(item) => item.label}
                        getOptionValue={(item) => item.value}
                        loadOptions={handleMaterialSearch}
                        noOptionsMessage={({ inputValue }) =>
                          !inputValue ? "Type Something..." : "No record found"
                        }
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        menuPlacement="bottom"
                        menuShouldScrollIntoView={false}
                        placeholder="Enter Material Name"
                        value={value}
                        isClearable
                        onChange={(selected) => onChange(selected)}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6} md={4} lg={4}>
                  <Controller
                    name="rackValue"
                    control={control}
                    render={({ field: { onChange, value, ref } }) => (
                      <AsyncSelect
                        ref={rackRef}
                        defaultValue={rackOptions}
                        styles={{
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999,
                          }),
                        }}
                        inputRef={ref}
                        getOptionLabel={(item) => item.label}
                        getOptionValue={(item) => item.value}
                        loadOptions={handleRackSearch}
                        noOptionsMessage={({ inputValue }) =>
                          !inputValue ? "Type Something..." : "No record found"
                        }
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        menuPlacement="bottom"
                        menuShouldScrollIntoView={false}
                        placeholder="Enter Rack Name"
                        value={value}
                        isClearable
                        onChange={(selected) => onChange(selected)}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={6} md={4} lg={4}>
                  <Controller
                    name="supplierValue"
                    control={control}
                    render={({ field: { onChange, value, ref } }) => (
                      <AsyncSelect
                        ref={supplierRef}
                        defaultValue={supplierOptions}
                        styles={{
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999,
                          }),
                        }}
                        inputRef={ref}
                        getOptionLabel={(item) => item.name}
                        getOptionValue={(item) => item?.id?.toString()}
                        loadOptions={handleSupplierSearch}
                        noOptionsMessage={({ inputValue }) =>
                          !inputValue ? "Type Something..." : "No record found"
                        }
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        menuPlacement="bottom"
                        menuShouldScrollIntoView={false}
                        placeholder="Enter Supplier Name"
                        value={value}
                        isClearable
                        onChange={(selected) => onChange(selected)}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6} md={4} lg={4}>
                  <DatePickerElement
                    control={control}
                    name="entryDate"
                    label="Entry Date"
                    format="DD-MM-YYYY"
                    sx={{ width: "100%" }}
                  ></DatePickerElement>
                </Grid>
                <Grid item xs={6} md={4} lg={4}>
                  <SelectField
                    options={sectionOptions}
                    control={control}
                    name="sectionID"
                    label="Section"
                    placeholder="Select Section"
                  />
                </Grid>

                <Grid item xs={6} md={4} lg={4}>
                  <SelectField
                    options={materialStatusOptions}
                    control={control}
                    name="materialStatusID"
                    label="Material Status"
                    placeholder="Select Material Status"
                  />
                </Grid>

                <Grid item xs={12} sm={12} md={4} lg={3}>
                  <SelectField
                    options={barcodeFormatOptions}
                    control={control}
                    name="barCodeFormats"
                    label="Barcode Format*"
                    placeholder="Select Barcode Format"
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={4} lg={3}>
                  <SelectField
                    options={contentFormats}
                    control={control}
                    name="contentFormats"
                    label="Content Format*"
                    placeholder="Select Content Type"
                  />
                </Grid>
                <Grid item xs={6} sm={6} md={6} lg={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isReportsChecked}
                        onChange={handleIsReportsChange}
                      />
                    }
                    {...register("isReports")}
                    label="Do You Want a Export with separate List ?"
                  />
                  <Tooltip
                    title="a saperate PDF will Generate with Generated Barcode (If Enabled)"
                    arrow
                  >
                    <InfoCircleFilled />
                  </Tooltip>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isBorderChecked}
                        onChange={handleIsBorderChange}
                      />
                    }
                    {...register("isBorder")}
                    label="Do You Want Border in Barcode ?"
                  />
                  <Tooltip
                    title="border will print in all side of barcode (If Enabled)"
                    arrow
                  >
                    <InfoCircleFilled />
                  </Tooltip>
                </Grid>

                <Grid item xs={12} md={12} lg={12} alignContent={"flex-end"}>
                  <RadioButtonGroup
                    row
                    control={control}
                    name="accessionValueType"
                    options={[
                      {
                        id: "1",
                        label: "Select Accession No.",
                      },
                      {
                        id: "2",
                        label: "Enter Multiple Accession No.",
                      },
                    ]}
                  />
                </Grid>
                <Grid item xs={12} md={12} lg={12}>
                  {accessionValueType == 2 && (
                    <>
                      <TextField
                        fullWidth
                        multiline
                        rows={6}
                        {...register("accessionNoStringValue")}
                        label="Accession No (separated by space, comma or new line)"
                        placeholder="Enter Accession no separated by space, comma or new line"
                        autoComplete="off"
                      />
                    </>
                  )}
                  {accessionValueType == 1 && (
                    <>
                      <Controller
                        name="accessionNo"
                        control={control}
                        render={({ field: { onChange, value, ref } }) => (
                          <AsyncSelect
                            defaultValue={accessionOptions}
                            styles={{
                              menuPortal: (base) => ({
                                ...base,
                                zIndex: 9999,
                              }),

                              control: (baseStyles) => ({
                                ...baseStyles,
                                borderColor: errors.accessionNo
                                  ? "red"
                                  : baseStyles.borderColor,
                              }),
                            }}
                            isMulti
                            inputRef={ref}
                            getOptionLabel={(item) => item.name}
                            getOptionValue={(item) => item.id}
                            loadOptions={handleAccessionSearch}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            menuPlacement="bottom"
                            menuShouldScrollIntoView={false}
                            placeholder="Select Accession No."
                            value={value}
                            isClearable
                            closeMenuOnSelect={false}
                            onChange={(selected) => onChange(selected)}
                            noOptionsMessage={({ inputValue }) =>
                              !inputValue
                                ? "Type Something..."
                                : "No record found"
                            }
                          />
                        )}
                      />
                      <FormHelperText sx={{ color: "red" }}>
                        {errors.accessionNo?.message}
                      </FormHelperText>
                    </>
                  )}
                </Grid>

                <Grid
                  container
                  item
                  direction={"row"}
                  xs={12}
                  md={6}
                  lg={6}
                  alignContent={"flex-end"}
                >
                  <AnimateButton>
                    <Tooltip
                      title="Downloading may take some time. Please do not refresh until the download is complete."
                      arrow
                    >
                      <LoadingButton
                        disableElevation
                        disabled={isSubmitting}
                        type="submit"
                        fullWidth
                        variant="contained"
                        loading={loading}
                        loadingPosition="start"
                        startIcon={<CloudDownloadOutlined />}
                      >
                        Download
                      </LoadingButton>
                    </Tooltip>
                  </AnimateButton>
                  <Button
                    title="Clear"
                    type="reset"
                    sx={{ mx: 1 }}
                    variant="contained"
                    color="secondary"
                    onClick={handleClearFilter}
                  >
                    Clear
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </>
        ) : (
          <></>
        )}
      </MainCard>
    </>
  );
};
export default AccessionBarCodes;
